import { modalClosed, selectModalStack } from '@/shared/store/reducers/modal/modal.slice';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

import { DialogStackProvider } from '../../ui/DialogStack/DialogStack';
import { modalRegistry } from '../modal-registry/modal-registry';

/**
 * Mounted ONCE, in the shell. Renders whatever the modal slice says is open.
 *
 * Every entry stays mounted while it is on the stack, so dismissing a nested
 * dialog returns the operator to its parent rather than to an empty page. Depth
 * comes from the array index and is published through `DialogStackProvider`,
 * which is what lets `DialogContent` recede a parent without any dialog having
 * to register itself.
 *
 * An unregistered type is SKIPPED rather than thrown on: a typo in one
 * `openModal('roleFrom')` call should cost that one dialog, not the whole
 * portal. The registry's own `register` throws on a genuine collision instead,
 * which is the failure worth being loud about.
 */
export function GlobalModalManager() {
  const stack = useAppSelector(selectModalStack);
  const dispatch = useAppDispatch();

  return (
    <>
      {stack.map((entry, index) => {
        const Modal = modalRegistry.resolve(entry.type);

        if (Modal === undefined) {
          return null;
        }

        return (
          <DialogStackProvider key={entry.type} depth={index} total={stack.length}>
            <Modal
              open
              modalType={entry.type}
              modalData={entry.data}
              onCancel={() => {
                dispatch(modalClosed(entry.type));
              }}
            />
          </DialogStackProvider>
        );
      })}
    </>
  );
}
